// F4c-3: product detail route (stitch-react — replaces placeholder shell).
//
// Responsibilities:
//   - Read `:productId` via useParams.
//   - Call `useProduct(productId)` — returns `data === null` for a genuine
//     "not found" (per design §8.3), not an error.
//   - Render one of four branches:
//       1. isLoading  → skeleton matching the gallery+metadata layout
//       2. isError    → inline error block with translated message
//       3. data === null (resolved) → friendly not-found card with CTA
//       4. data present → <ProductDetailView product={data} />
//   - Own the `DeleteProductDialog` open state. The dialog component itself
//     lands in F4c-5; this route exposes an `onDeleteClick` callback to the
//     view that flips local state, and keeps a TODO for F4c-5 to import
//     `<DeleteProductDialog>` and render it conditionally.
//
// This route is already wrapped in `<AuthGuard mode="require-store">` by
// F-002's router, so no authentication logic is needed here.

import { useCallback, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAuthSession, useStoreCategories, useTranslation } from '@dukanti/shared';
import { supabase } from '@/lib/supabase';
import { useCurrentStore } from '@/hooks/use-current-store';
import { useProduct } from '@/hooks/use-product';
import { useStore } from '@/features/store-settings/use-store';
import { ProductDetailView } from '@/features/products/components/product-detail-view';
import { DeleteProductDialog } from '@/features/products/components/delete-product-dialog';
import { PublishProductDialog } from '@/features/products/components/publish-product-dialog';
import { UnpublishProductDialog } from '@/features/products/components/unpublish-product-dialog';
import { SkeletonBar } from '@/features/products/components/skeleton-bar';
import { ProductNotFoundCard } from '@/features/products/components/product-not-found-card';
import { stockReturnPath } from '@/features/stock/lib/stock-status';
import { ProductInlineError } from '@/features/products/components/product-inline-error';

export function ProductDetailRoute(): React.JSX.Element {
  const { t, dir } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const returnToStock = stockReturnPath(location.state);
  const { productId } = useParams<{ productId: string }>();
  const authSession = useAuthSession(supabase);
  const { storeId } = useCurrentStore(authSession);
  const { data, isLoading, isError, refetch } = useProduct(productId);
  const categories = useStoreCategories(supabase, storeId);

  // F-028: resolve storeSlug + storePublished so the Publish dialog can
  // build the public URL preview and the detail-view Publish button can
  // enforce its parent-store-first gate. Matches the dashboard-route
  // pattern (useStore over ['store', storeId]).
  const store = useStore(storeId);
  const storeSlug = store.data?.slug ?? null;
  const storePublished = store.data?.published === true;

  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isPublishDialogOpen, setIsPublishDialogOpen] = useState(false);
  const [isUnpublishDialogOpen, setIsUnpublishDialogOpen] = useState(false);
  const handleDeleteClick = useCallback(() => {
    setIsDeleteDialogOpen(true);
  }, []);
  const handlePublishClick = useCallback(() => {
    setIsPublishDialogOpen(true);
  }, []);
  const handleUnpublishClick = useCallback(() => {
    setIsUnpublishDialogOpen(true);
  }, []);

  const handleDeleted = useCallback(() => {
    navigate('/products', { replace: true });
  }, [navigate]);

  return (
    <main dir={dir} data-testid="product-detail-route" className="bg-background min-h-screen">
      <div className="mx-auto max-w-screen-xl px-4 py-8 sm:px-6 lg:px-8">
        {returnToStock ? (
          <Link
            to={returnToStock}
            className="text-foreground focus-visible:ring-ring mb-4 inline-flex min-h-11 items-center rounded-md px-3 text-sm underline underline-offset-4 focus-visible:outline-none focus-visible:ring-2"
          >
            {t('admin.stock.worklist.backToStock')}
          </Link>
        ) : null}
        {isLoading ? (
          <ProductDetailSkeleton loadingLabel={t('admin.products.detail.loadingLabel')} />
        ) : isError ? (
          <ProductInlineError
            title={t('common.error')}
            retryLabel={t('common.retry')}
            backLabel={t('admin.products.detail.notFoundCta')}
            onRetry={() => {
              void refetch();
            }}
            testIdPrefix="product-detail"
          />
        ) : data === null ? (
          <ProductNotFoundCard
            title={t('admin.products.detail.notFoundTitle')}
            description={t('admin.products.detail.notFoundDescription')}
            ctaLabel={t('admin.products.detail.notFoundCta')}
            testIdPrefix="product-detail"
          />
        ) : data ? (
          <ProductDetailView
            product={data}
            onDeleteClick={handleDeleteClick}
            onPublishClick={handlePublishClick}
            onUnpublishClick={handleUnpublishClick}
            storePublished={storePublished}
          />
        ) : null}
      </div>
      {storeId ? (
        <DeleteProductDialog
          product={data ?? null}
          storeId={storeId}
          open={isDeleteDialogOpen}
          onOpenChange={setIsDeleteDialogOpen}
          onDeleted={handleDeleted}
        />
      ) : null}
      {/* F-028: publish / unpublish dialogs. Mounted only when both the
          product and the store slug are known so the URL preview is
          non-null. */}
      {data && storeSlug ? (
        <>
          <PublishProductDialog
            productId={data.id}
            storeSlug={storeSlug}
            open={isPublishDialogOpen}
            onOpenChange={setIsPublishDialogOpen}
            inventory={data.inventory}
            hasCategories={categories.categories.length > 0}
          />
          <UnpublishProductDialog
            productId={data.id}
            storeSlug={storeSlug}
            open={isUnpublishDialogOpen}
            onOpenChange={setIsUnpublishDialogOpen}
          />
        </>
      ) : null}
    </main>
  );
}

// ---------- Loading skeleton ----------

interface ProductDetailSkeletonProps {
  readonly loadingLabel: string;
}

function ProductDetailSkeleton({ loadingLabel }: ProductDetailSkeletonProps) {
  return (
    <div
      className="flex flex-col gap-8"
      role="status"
      aria-busy="true"
      aria-live="polite"
      data-testid="product-detail-skeleton"
    >
      <span className="sr-only">{loadingLabel}</span>
      {/* Header row */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <SkeletonBar className="h-4 w-24" />
        <div className="flex items-center gap-2">
          <SkeletonBar className="rounded-btn h-10 w-24" />
          <SkeletonBar className="rounded-btn h-10 w-24" />
        </div>
      </div>
      {/* Body */}
      <div className="grid grid-cols-1 gap-8 lg:grid-cols-[minmax(0,1.15fr)_minmax(0,1fr)] lg:items-start">
        <div className="flex flex-col gap-3">
          <SkeletonBar className="rounded-card aspect-[4/3] w-full" />
          <div className="flex gap-2">
            <SkeletonBar className="h-16 w-16 rounded-lg" />
            <SkeletonBar className="h-16 w-16 rounded-lg" />
            <SkeletonBar className="h-16 w-16 rounded-lg" />
          </div>
        </div>
        <div className="border-border bg-card shadow-admin-card flex flex-col gap-4 rounded-lg border p-6">
          <SkeletonBar className="h-5 w-16" />
          <SkeletonBar className="h-7 w-4/5" />
          <SkeletonBar className="h-8 w-28" />
          <SkeletonBar className="h-4 w-24" />
          <div className="bg-border h-px w-full" aria-hidden="true" />
          <SkeletonBar className="h-4 w-16" />
          <SkeletonBar className="h-20 w-full" />
        </div>
      </div>
    </div>
  );
}
